import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  Plus,
  Calendar,
  Search,
  Filter,
  ChevronDown,
  AlertCircle
} from 'lucide-react';
import { authAPI } from '../../services/api';

export default function ExpertWithdrawals() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [walletLoading, setWalletLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [toast, setToast] = useState({ show: false, type: '', message: '' });

  const [formData, setFormData] = useState({
    amount: '',
    bank_name: '',
    account_number: '',
    account_name: ''
  });

  const [errors, setErrors] = useState({});

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => {
      setToast({ show: false, type: '', message: '' });
    }, 3000);
  };

  // Fetch withdrawals and wallet balance
  useEffect(() => {
    fetchWithdrawals();
    fetchWalletBalance();
  }, []);

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      const result = await authAPI.getExpertWithdrawals();
      
      if (result.success) {
        setWithdrawals(result.data || []);
      } else {
        // Show the error message from API response
        showToast('error', result.message || 'Failed to fetch withdrawals');
      }
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      
      // Check if error has response data with message
      let errorMessage = 'Failed to fetch withdrawals';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showToast('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletBalance = async () => {
    try {
      setWalletLoading(true);
      const result = await authAPI.getExpertWallet();
      
      if (result.success) {
        setWallet(result.data);
      } else {
        console.error('Failed to fetch wallet balance:', result.message);
      }
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
    } finally {
      setWalletLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }

    if (!formData.bank_name.trim()) {
      newErrors.bank_name = 'Bank name is required';
    }

    if (!formData.account_number.trim()) {
      newErrors.account_number = 'Account number is required';
    }

    if (!formData.account_name.trim()) {
      newErrors.account_name = 'Account name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      
      const withdrawalData = {
        amount: parseFloat(formData.amount),
        bank_name: formData.bank_name.trim(),
        account_number: formData.account_number.trim(),
        account_name: formData.account_name.trim()
      };

      const result = await authAPI.createExpertWithdrawal(withdrawalData);
      
      if (result.success) {
        // Show the success message from API response
        showToast('success', result.message || 'Withdrawal request submitted successfully');
        
        // Add the new withdrawal to the list (optimistic update)
        const newWithdrawal = {
          ...result.data,
          created_at: new Date().toISOString()
        };
        setWithdrawals(prev => [newWithdrawal, ...prev]);
        
        // Reset form
        setFormData({
          amount: '',
          bank_name: '',
          account_number: '',
          account_name: ''
        });
        setShowForm(false);
        setErrors({});
        
        // Refresh wallet balance after successful withdrawal
        fetchWalletBalance();
      } else {
        // Show the error message from API response
        showToast('error', result.message || 'Failed to submit withdrawal request');
      }
    } catch (error) {
      console.error('Error submitting withdrawal:', error);
      
      // Check if error has response data with message
      let errorMessage = 'Failed to submit withdrawal request';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showToast('error', errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        icon: Clock,
        className: 'status-pending',
        text: 'Pending'
      },
      approved: {
        icon: CheckCircle,
        className: 'status-approved',
        text: 'Approved'
      },
      rejected: {
        icon: XCircle,
        className: 'status-rejected',
        text: 'Rejected'
      },
      completed: {
        icon: CheckCircle,
        className: 'status-completed',
        text: 'Completed'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;

    return (
      <span className={`status-badge ${config.className}`}>
        <IconComponent size={14} />
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  const filteredWithdrawals = withdrawals.filter(withdrawal => {
    const matchesSearch =
      withdrawal.bank_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      withdrawal.account_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      withdrawal.account_number?.includes(searchTerm);

    const matchesStatus = statusFilter === 'All' || withdrawal.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const totalWithdrawn = withdrawals
    .filter(w => w.status === 'completed')
    .reduce((sum, w) => sum + parseFloat(w.amount || 0), 0);

  const pendingAmount = withdrawals
    .filter(w => w.status === 'pending')
    .reduce((sum, w) => sum + parseFloat(w.amount || 0), 0);

  if (loading) {
    return (
      <div className="withdrawals-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading withdrawals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="withdrawals-container">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast-notification-fxk ${toast.type === 'success' ? 'toast-success-fxk' : 'toast-error-fxk'}`}>
          <div className="toast-content-fxk">
            <div className="toast-icon-fxk">
              {toast.type === 'success' ? '✓' : '✗'}
            </div>
            <div className="toast-message-fxk">{toast.message}</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="withdrawals-header">
        <div className="header-content">
          <h1>Withdrawals</h1>
          <p>Manage your withdrawal requests and track payment history</p>
        </div>

        <button
          className="add-withdrawal-btn"
          onClick={() => setShowForm(true)}
        >
          <Plus size={20} />
          New Withdrawal
        </button>
      </div>

      {/* Wallet Balance Card */}
      <div className="wallet-balance-card">
        <div className="wallet-header">
          <h2>Wallet Balance</h2>
          <button 
            className="refresh-btn"
            onClick={fetchWalletBalance}
            disabled={walletLoading}
          >
            {walletLoading ? '...' : '↻'}
          </button>
        </div>
        
        {walletLoading ? (
          <div className="wallet-loading">Loading wallet balance...</div>
        ) : wallet ? (
          <div className="wallet-stats">
            <div className="wallet-item">
              <span className="wallet-label">Available Balance</span>
              <span className="wallet-amount available">{formatCurrency(parseFloat(wallet.available_balance))}</span>
            </div>
            <div className="wallet-item">
              <span className="wallet-label">Total Earned</span>
              <span className="wallet-amount earned">{formatCurrency(parseFloat(wallet.total_earned))}</span>
            </div>
            <div className="wallet-item">
              <span className="wallet-label">Pending Payments</span>
              <span className="wallet-amount pending">{formatCurrency(parseFloat(wallet.pending_payments))}</span>
            </div>
          </div>
        ) : (
          <div className="wallet-error">Failed to load wallet balance</div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="withdrawal-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <CheckCircle className="icon-success" />
          </div>
          <div className="stat-content">
            <h3>Total Withdrawn</h3>
            <p className="stat-amount">{formatCurrency(totalWithdrawn)}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Clock className="icon-warning" />
          </div>
          <div className="stat-content">
            <h3>Pending</h3>
            <p className="stat-amount">{formatCurrency(pendingAmount)}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <CreditCard className="icon-info" />
          </div>
          <div className="stat-content">
            <h3>Total Requests</h3>
            <p className="stat-amount">{withdrawals.length}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="withdrawals-filters">
        <div className="search-container">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by bank name, account name, or number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-container">
          <Filter size={20} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="All">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Withdrawals Table */}
      <div className="withdrawals-table-container">
        {filteredWithdrawals.length === 0 ? (
          <div className="empty-state">
            <DollarSign size={48} className="empty-icon" />
            <h3>No withdrawals found</h3>
            <p>
              {withdrawals.length === 0
                ? "You haven't made any withdrawal requests yet."
                : "No withdrawals match your current filters."
              }
            </p>
            {withdrawals.length === 0 && (
              <button
                className="empty-action-btn"
                onClick={() => setShowForm(true)}
              >
                Create First Withdrawal
              </button>
            )}
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="withdrawals-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Bank Details</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Processed</th>
                </tr>
              </thead>
              <tbody>
                {filteredWithdrawals.map((withdrawal) => (
                  <tr key={withdrawal.id} className="withdrawal-row">
                    <td>
                      <div className="date-cell">
                        <Calendar size={16} />
                        {formatDate(withdrawal.created_at)}
                      </div>
                    </td>
                    <td>
                      <div className="amount-cell">
                        {formatCurrency(withdrawal.amount)}
                      </div>
                    </td>
                    <td>
                      <div className="bank-details">
                        <div className="bank-name">{withdrawal.bank_name}</div>
                        <div className="account-info">
                          {withdrawal.account_name} • {withdrawal.account_number}
                        </div>
                      </div>
                    </td>
                    <td>
                      {getStatusBadge(withdrawal.status)}
                    </td>
                    <td>
                      <div className="notes-cell">
                        {withdrawal.notes || '-'}
                      </div>
                    </td>
                    <td>
                      <div className="processed-date">
                        {withdrawal.processed_at
                          ? formatDate(withdrawal.processed_at)
                          : '-'
                        }
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Withdrawal Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content withdrawal-form-modal">
            <div className="modal-header">
              <h2>New Withdrawal Request</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowForm(false);
                  setErrors({});
                  setFormData({
                    amount: '',
                    bank_name: '',
                    account_number: '',
                    account_name: ''
                  });
                }}
              >
                ×
              </button>
            </div>
      
            <form onSubmit={handleSubmit} className="withdrawal-form">
              <div className="form-group">
                <label htmlFor="amount">Withdrawal Amount *</label>
                <div className="input-with-icon">
                  <div size={20} className="input-icon">₦</div>
                  <input
                    type="number"
                    id="amount"
                    value={formData.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    placeholder="Enter amount"
                    min="1000"
                    step="0.01"
                    className={errors.amount ? 'error' : ''}
                  />
                </div>
                {errors.amount && <span className="error-text">{errors.amount}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="bank_name">Bank Name *</label>
                <input
                  type="text"
                  id="bank_name"
                  value={formData.bank_name}
                  onChange={(e) => handleInputChange('bank_name', e.target.value)}
                  placeholder="Enter bank name"
                  className={errors.bank_name ? 'error' : ''}
                />
                {errors.bank_name && <span className="error-text">{errors.bank_name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="account_number">Account Number *</label>
                <input
                  type="text"
                  id="account_number"
                  value={formData.account_number}
                  onChange={(e) => handleInputChange('account_number', e.target.value)}
                  placeholder="Enter account number"
                  className={errors.account_number ? 'error' : ''}
                />
                {errors.account_number && <span className="error-text">{errors.account_number}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="account_name">Account Name *</label>
                <input
                  type="text"
                  id="account_name"
                  value={formData.account_name}
                  onChange={(e) => handleInputChange('account_name', e.target.value)}
                  placeholder="Enter account holder name"
                  className={errors.account_name ? 'error' : ''}
                />
                {errors.account_name && <span className="error-text">{errors.account_name}</span>}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowForm(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <div className="spinner-small"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <div size={20}>₦</div>
                      Submit Request
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .withdrawals-container {
          width: auto;
        }

        .withdrawals-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 32px;
        }

        .wallet-balance-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e5e7eb;
          padding: 24px;
          margin-bottom: 32px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .wallet-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .wallet-header h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #1a1a1a;
        }

        .refresh-btn {
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 16px;
          transition: all 0.2s;
        }

        .refresh-btn:hover:not(:disabled) {
          background: #e5e7eb;
        }

        .refresh-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .wallet-loading, .wallet-error {
          text-align: center;
          padding: 20px;
          color: #666;
          font-style: italic;
        }

        .wallet-error {
          color: #ef4444;
        }

        .wallet-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
        }

        .wallet-item {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 12px;
          border: 1px solid #f3f4f6;
        }

        .wallet-label {
          font-size: 14px;
          color: #666;
          font-weight: 500;
        }

        .wallet-amount {
          font-size: 20px;
          font-weight: 600;
        }

        .wallet-amount.available {
          color: #22c55e;
        }

        .wallet-amount.earned {
          color: #3b82f6;
        }

        .wallet-amount.pending {
          color: #f59e0b;
        }

        .header-content h1 {
          margin: 0 0 8px 0;
          font-size: 28px;
          font-weight: 600;
          color: #1a1a1a;
        }

        .header-content p {
          margin: 0;
          color: #666;
          font-size: 16px;
        }

        .add-withdrawal-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: #0e4410;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }

        .add-withdrawal-btn:hover {
          background: #0f4211ff;
          transform: translateY(-1px);
        }

        .withdrawal-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: white;
          padding: 24px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .icon-success {
          color: #22c55e;
          background: #dcfce7;
        }

        .icon-warning {
          color: #f59e0b;
          background: #fef3c7;
        }

        .icon-info {
          color: #3b82f6;
          background: #dbeafe;
        }

        .stat-content h3 {
          margin: 0 0 4px 0;
          font-size: 14px;
          color: #666;
          font-weight: 500;
        }

        .stat-amount {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #1a1a1a;
        }

        .withdrawals-filters {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }

        .search-container, .filter-container {
          display: flex;
          align-items: center;
          gap: 8px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 12px 16px;
        }

        .search-container {
          flex: 1;
          min-width: 250px;
        }

        .search-input, .filter-select {
          border: none;
          outline: none;
          background: transparent;
          width: 100%;
        }

        .filter-select {
          min-width: 120px;
        }

        .withdrawals-table-container {
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }

        .table-wrapper {
          overflow-x: auto;
        }

        .withdrawals-table {
          width: 100%;
          border-collapse: collapse;
        }

        .withdrawals-table th {
          background: #f9fafb;
          padding: 16px 20px;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
        }

        .withdrawals-table td {
          padding: 16px 20px;
          border-bottom: 1px solid #f3f4f6;
        }

        .withdrawal-row:hover {
          background: #f9fafb;
        }

        .date-cell, .amount-cell {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #374151;
        }

        .bank-details .bank-name {
          font-weight: 500;
          color: #1a1a1a;
          margin-bottom: 4px;
        }

        .bank-details .account-info {
          font-size: 14px;
          color: #666;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
        }

        .status-pending {
          background: #fef3c7;
          color: #f59e0b;
        }

        .status-approved {
          background: #dcfce7;
          color: #22c55e;
        }

        .status-completed {
          background: #dcfce7;
          color: #16a34a;
        }

        .status-rejected {
          background: #fee2e2;
          color: #ef4444;
        }

        .notes-cell, .processed-date {
          font-size: 14px;
          color: #666;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-icon {
          color: #d1d5db;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          margin: 0 0 8px 0;
          color: #374151;
        }

        .empty-state p {
          margin: 0 0 24px 0;
          color: #6b7280;
        }

        .empty-action-btn {
          padding: 12px 24px;
          background: #22c55e;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .withdrawal-form-modal {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 500px;
          max-height: 80vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px 24px 0 24px;
          margin-bottom: 24px;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #666;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .withdrawal-form {
          padding: 0 24px 24px 24px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #374151;
        }

        .input-with-icon {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 12px;
          color: #666;
        }

        .form-group input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 16px;
          transition: border-color 0.2s;
        }

        .input-with-icon input {
          padding-left: 44px;
        }

        .form-group input:focus {
          outline: none;
          border-color: #22c55e;
        }

        .form-group input.error {
          border-color: #ef4444;
        }

        .error-text {
          color: #ef4444;
          font-size: 14px;
          margin-top: 4px;
          display: block;
        }

        .form-actions {
          display: flex;
          gap: 12px;
          margin-top: 32px;
        }

        .cancel-btn, .submit-btn {
          flex: 1;
          padding: 12px 20px;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .cancel-btn {
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #e5e7eb;
        }

        .submit-btn {
          background: #0e4410;
          color: white;
          border: none;
        }

        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-note {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 16px 24px 24px 24px;
          background: #fef3c7;
          margin: 0 -24px -24px -24px;
          border-radius: 0 0 12px 12px;
        }

        .form-note p {
          margin: 0;
          font-size: 14px;
          color: #92400e;
        }

        .loading-spinner {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f4f6;
          border-top: 4px solid #22c55e;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        .spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .toast-notification-fxk {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 1001;
          border-radius: 8px;
          padding: 16px 20px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          animation: slideIn 0.3s ease-out;
        }

        .toast-success-fxk {
          background: #22c55e;
          color: white;
        }

        .toast-error-fxk {
          background: #ef4444;
          color: white;
        }

        .toast-content-fxk {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .toast-icon-fxk {
          font-weight: bold;
          font-size: 16px;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @media (max-width: 768px) {
  
          .withdrawals-header {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
          }

          .withdrawal-stats {
            grid-template-columns: 1fr;
          }

          .withdrawals-filters {
            flex-direction: column;
          }

          .search-container {
            min-width: auto;
          }

          .withdrawals-table {
            font-size: 14px;
          }

          .withdrawals-table th,
          .withdrawals-table td {
            padding: 12px 16px;
          }

          .modal-overlay {
            padding: 10px;
          }

          .form-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}
